/**
 * Training Page Component - Secure PDF and Video Training Modules
 * 
 * Provides interactive options: Read Training PDF and Watch Training Videos.
 * Includes copy-protection, screenshot mitigation, and custom simulated players.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './TrainingPage.css';
import { getBackendUrl } from '../utils/config';
import { ACADEMY_AUTH_TOKEN_KEY } from '../utils/authStorage';
import { socketService } from '../services/socketService';
import SupportCard from './SupportCard';
import authService from '../services/authService';

// Video playlists are fetched from the backend API — never hardcoded in frontend

const documentIndexSections = [
  {
    title: "Introduction",
    page: 1,
    items: [
      { title: "Cover Page", page: 1 },
      { title: "Table of Contents", page: 2 }
    ]
  },
  {
    title: "Part 1: Welcome & Universal Setup",
    page: 3,
    items: [
      { title: "1. Welcome to Stenope", page: 3 },
      { title: "2. Choose Your Workflow", page: 4 },
      { title: "3. Hardware Requirements", page: 5 },
      { title: "4. Universal Setup Overview", page: 6 },
      { title: "4.1 General Best Practices", page: 7 },
      { title: "4.2 Login & Create a Session", page: 8 },
      { title: "4.3 Download Desktop App", page: 9 },
      { title: "4.4 Session Name & Layout", page: 10 },
      { title: "4.5 Add Speakers & Hotkeys", page: 11 },
      { title: "4.6 Configure Audio Channels", page: 12 },
      { title: "4.7 Review & Customize Insertions", page: 13 },
      { title: "4.8 Custom Insertions & Hotkeys", page: 14 },
      { title: "4.9 Bookmark Transcript Locations", page: 15 },
      { title: "4.10 Configure Keyboard Hotkeys", page: 16 },
      { title: "4.11 Pre-Proceeding Checklist", page: 17 }
    ]
  },
  {
    title: "Part 2: Core Training Modes",
    page: 18,
    items: [
      // Annotation Mode
      { title: "5. Annotation Mode Overview", page: 18 },
      { title: "5.1 Mode Settings & Hotkeys", page: 19 },
      { title: "5.2 Run a Proceeding in Annotation", page: 20 },
      { title: "5.3 Q&A and Exhibits in Annotation", page: 21 },
      { title: "5.4 Using Stenope Intelligence", page: 22 },
      // Cloud Realtime
      { title: "6. Cloud Transcription Overview", page: 23 },
      { title: "6.1 Enable Cloud & Speakers", page: 24 },
      { title: "6.2 Q&A, Exhibits & Parentheticals", page: 25 },
      { title: "6.3 Marked Words & Auto-Diarization", page: 26 },
      // Stenope Voice
      { title: "7. Stenope Voice Overview", page: 27 },
      { title: "7.1 Enable Voice Mode & Commands", page: 28 },
      { title: "7.2 Detach Audio and Q&A", page: 29 },
      { title: "7.3 Marked Words and Briefs", page: 30 },
      { title: "7.4 Exhibits, Insertions & Parentheticals", page: 31 }
    ]
  },
  {
    title: "Part 3: Shared Tools & Utilities",
    page: 32,
    items: [
      { title: "8. Shared Tools Overview", page: 32 },
      { title: "8.1 Dictionaries Setup", page: 33 },
      { title: "8.2 Offline Mode & Realtime Attorney View", page: 34 },
      { title: "8.3 Audio Playback & Scoping", page: 35 },
      { title: "8.4 Export Your Transcript", page: 36 },
      { title: "8.5 Session History & Templates", page: 37 },
      { title: "8.6 Local Database & Session Reloads", page: 38 },
      { title: "8.7 Download Multi-Channel Audio & Clips", page: 39 }
    ]
  },
  {
    title: "Part 4: Troubleshooting & Legal",
    page: 40,
    items: [
      { title: "9. Troubleshooting & Best Practices", page: 40 },
      { title: "10. Legal Notices", page: 41 }
    ]
  }
];

const voiceDocumentIndexSections = [
  {
    title: "Introduction",
    page: 1,
    items: [
      { title: "Cover Page", page: 1 },
      { title: "Table of Contents", page: 2 }
    ]
  },
  {
    title: "Part 1: Welcome & Universal Setup",
    page: 3,
    items: [
      { title: "4. Part 1: Universal Setup Overview", page: 3 },
      { title: "4.1 Login and Create a New Session", page: 4 },
      { title: "4.2 Download and Install Desktop App", page: 5 },
      { title: "4.3 Name Session & Layout Setup", page: 6 },
      { title: "4.4 Add Speakers & Speaker Hotkeys", page: 7 },
      { title: "4.5 Configure Audio Channels", page: 8 },
      { title: "4.6 Review Insertions & Parentheticals", page: 9 },
      { title: "4.7 Custom Insertions & Hotkeys", page: 10 },
      { title: "4.8 Bookmark Transcript Locations", page: 11 },
      { title: "4.9 Configure Hotkeys", page: 12 },
      { title: "4.10 First Proceeding Checklist", page: 13 }
    ]
  },
  {
    title: "Part 2: Stenope Voice Workflow",
    page: 14,
    items: [
      { title: "7. Part 2C: Stenope Voice Overview", page: 14 },
      { title: "7.1 Enable Voice Mode & Commands", page: 15 },
      { title: "7.2 Detach Audio and Q & A Setup", page: 16 },
      { title: "7.3 Marked Words and Briefs", page: 17 },
      { title: "7.4 Exhibits, Insertions & Parentheticals", page: 18 }
    ]
  },
  {
    title: "Part 3: Shared Tools & Utilities",
    page: 19,
    items: [
      { title: "8. Part 3: Shared Tools Overview", page: 19 },
      { title: "8.1 Offline Mode & Attorney View", page: 20 },
      { title: "8.2 Audio Playback & Basic Scoping", page: 21 },
      { title: "8.3 Export Your Transcript", page: 22 },
      { title: "8.4 Session History, Templates & Sharing", page: 23 },
      { title: "8.5 Download Multi-Channel Audio & Clips", page: 24 }
    ]
  },
  {
    title: "Part 4: Troubleshooting & Legal",
    page: 25,
    items: [
      { title: "9. Troubleshooting & Best Practices", page: 25 },
      { title: "10. Legal Notices", page: 26 }
    ]
  }
];

// ============================================================
// CUSTOM YOUTUBE-STYLE VIDEO PLAYER COMPONENT
// ============================================================
const CustomVideoPlayer = ({ src, onNext, hasNext }) => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [showSpeedMenu, setShowSpeedMenu] = useState(false);
    const [isLoadingVideo, setIsLoadingVideo] = useState(true);
    
    // For seeking/tap overlays
    const [overlay, setOverlay] = useState({ visible: false, type: '', text: '', key: 0 });

    // Auto-hide controls timer
    const controlsTimeoutRef = useRef(null);

    // Reset controls timeout on mouse movement
    const handleMouseMove = () => {
        setShowControls(true);
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }
        controlsTimeoutRef.current = setTimeout(() => {
            if (isPlaying) {
                setShowControls(false);
                setShowSpeedMenu(false);
            }
        }, 3000);
    };

    useEffect(() => {
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
        };
    }, [isPlaying]);

    // Handle video state changes
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onTimeUpdate = () => setCurrentTime(video.currentTime);
        const onDurationChange = () => setDuration(video.duration);
        const onVolumeChange = () => {
            setVolume(video.volume);
            setIsMuted(video.muted);
        };

        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPause);
        video.addEventListener('timeupdate', onTimeUpdate);
        video.addEventListener('durationchange', onDurationChange);
        video.addEventListener('volumechange', onVolumeChange);

        // Reset state on source change
        setIsPlaying(false);
        setCurrentTime(0);
        setPlaybackRate(1);
        video.playbackRate = 1;
        setIsLoadingVideo(true);

        return () => {
            if (video) {
                video.removeEventListener('play', onPlay);
                video.removeEventListener('pause', onPause);
                video.removeEventListener('timeupdate', onTimeUpdate);
                video.removeEventListener('durationchange', onDurationChange);
                video.removeEventListener('volumechange', onVolumeChange);
            }
        };
    }, [src]);

    // Handle Overlay disappear
    useEffect(() => {
        if (overlay.visible) {
            const t = setTimeout(() => {
                setOverlay(prev => ({ ...prev, visible: false }));
            }, 800);
            return () => clearTimeout(t);
        }
    }, [overlay.visible, overlay.key]);

    const triggerOverlay = (type, text) => {
        setOverlay({ visible: true, type, text, key: Date.now() });
    };

    const togglePlay = () => {
        const video = videoRef.current;
        if (!video) return;

        if (video.paused) {
            video.play().catch(err => console.log("Play interrupted:", err));
            triggerOverlay('play', '▶');
        } else {
            video.pause();
            triggerOverlay('pause', '❚❚');
        }
        handleMouseMove();
    };

    const seek = (seconds) => {
        const video = videoRef.current;
        if (!video) return;
        
        let target = video.currentTime + seconds;
        if (target < 0) target = 0;
        if (target > video.duration) target = video.duration;
        video.currentTime = target;
        
        if (seconds > 0) {
            triggerOverlay('forward', `+${seconds} sec`);
        } else {
            triggerOverlay('backward', `${seconds} sec`);
        }
        handleMouseMove();
    };

    const handleProgressClick = (e) => {
        const video = videoRef.current;
        if (!video || !video.duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pos = (e.clientX - rect.left) / rect.width;
        video.currentTime = pos * video.duration;
    };

    const toggleMute = () => {
        const video = videoRef.current;
        if (!video) return;
        video.muted = !video.muted;
        handleMouseMove();
    };

    const handleVolumeSlider = (e) => {
        const video = videoRef.current;
        if (!video) return;
        const val = parseFloat(e.target.value);
        video.volume = val;
        video.muted = val === 0;
        handleMouseMove();
    };

    const handleSpeedChange = (rate) => {
        const video = videoRef.current;
        if (!video) return;
        video.playbackRate = rate;
        setPlaybackRate(rate);
        setShowSpeedMenu(false);
        handleMouseMove();
    };

    const toggleFullscreen = () => {
        const container = containerRef.current;
        if (!container) return;

        if (!document.fullscreenElement) {
            container.requestFullscreen().then(() => {
                setIsFullscreen(true);
            }).catch(err => {
                console.error("Error entering fullscreen:", err);
            });
        } else {
            document.exitFullscreen().then(() => {
                setIsFullscreen(false);
            });
        }
        handleMouseMove();
    };

    // Fullscreen change listener (handles Esc key too)
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Global and local Key listener when player is visible
    useEffect(() => {
        const handleKeyDown = (e) => {
            const active = document.activeElement;
            if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) {
                return;
            }

            const video = videoRef.current;
            if (!video) return;

            switch(e.key.toLowerCase()) {
                case ' ':
                case 'k':
                    e.preventDefault();
                    togglePlay();
                    break;
                case 'arrowleft':
                case 'j':
                    e.preventDefault();
                    seek(-10);
                    break;
                case 'arrowright':
                case 'l':
                    e.preventDefault();
                    seek(10);
                    break;
                case 'arrowup':
                    e.preventDefault();
                    video.volume = Math.min(1, video.volume + 0.1);
                    break;
                case 'arrowdown':
                    e.preventDefault();
                    video.volume = Math.max(0, video.volume - 0.1);
                    break;
                case 'f':
                    e.preventDefault();
                    toggleFullscreen();
                    break;
                case 'm':
                    e.preventDefault();
                    toggleMute();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [src]);

    // Format time (e.g. 85 -> "01:25")
    const formatTime = (secs) => {
        if (isNaN(secs)) return '00:00';
        const m = Math.floor(secs / 60);
        const s = Math.floor(secs % 60);
        const mm = m < 10 ? `0${m}` : m;
        const ss = s < 10 ? `0${s}` : s;
        return `${mm}:${ss}`;
    };

    const progressPercent = duration ? (currentTime / duration) * 100 : 0;

    return (
        <div 
            ref={containerRef}
            className={`youtube-player-container ${isFullscreen ? 'fullscreen-mode' : ''} ${!showControls ? 'hide-cursor' : ''}`}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => isPlaying && setShowControls(false)}
        >
            <video
                ref={videoRef}
                className="youtube-video-element"
                src={src}
                onClick={togglePlay}
                onDoubleClick={toggleFullscreen}
                onContextMenu={(e) => e.preventDefault()}
                onError={(e) => {
                    setIsLoadingVideo(false);
                    console.error("VIDEO ERROR: Code:", e.target.error ? e.target.error.code : "N/A", "Message:", e.target.error ? e.target.error.message : "");
                }}
                onLoadStart={() => {
                    setIsLoadingVideo(true);
                    console.log("VIDEO EVENT: loadstart for", src);
                }}
                onWaiting={() => setIsLoadingVideo(true)}
                onPlaying={() => setIsLoadingVideo(false)}
                onCanPlay={() => setIsLoadingVideo(false)}
                onCanPlayThrough={() => setIsLoadingVideo(false)}
                onSeeking={() => setIsLoadingVideo(true)}
                onSeeked={() => setIsLoadingVideo(false)}
                onStalled={() => setIsLoadingVideo(true)}
                playsInline
            />

            {/* Video Loading Animation Overlay */}
            {isLoadingVideo && (
                <div className="video-loading-overlay">
                    <div className="video-loading-spinner-container">
                        <div className="video-loading-spinner-ring"></div>
                        <div className="video-loading-spinner-ring inner"></div>
                    </div>
                    <div className="video-loading-text">Loading Video...</div>
                </div>
            )}

            {/* Ripple/Indicator Overlays */}
            {overlay.visible && (
                <div className={`video-action-overlay overlay-type-${overlay.type}`}>
                    {overlay.type === 'forward' && (
                        <div className="overlay-indicator right-indicator">
                            <span className="arrow-pulse">▶▶▶</span>
                            <span className="overlay-text">{overlay.text}</span>
                        </div>
                    )}
                    {overlay.type === 'backward' && (
                        <div className="overlay-indicator left-indicator">
                            <span className="arrow-pulse">◀◀◀</span>
                            <span className="overlay-text">{overlay.text}</span>
                        </div>
                    )}
                    {(overlay.type === 'play' || overlay.type === 'pause') && (
                        <div className="center-ripple-indicator">
                            <span className="center-icon">{overlay.text}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Controls Bar Overlay */}
            <div className={`youtube-controls-wrapper ${showControls ? 'visible' : ''}`}>
                {/* 1. Progress Bar (Scrubber) */}
                <div className="yt-progress-container" onClick={handleProgressClick}>
                    <div className="yt-progress-bar">
                        <div className="yt-progress-hover-line"></div>
                        <div className="yt-progress-played" style={{ width: `${progressPercent}%` }}>
                            <div className="yt-progress-scrubber-handle"></div>
                        </div>
                    </div>
                </div>

                {/* 2. Controls Row */}
                <div className="yt-controls-row">
                    <div className="yt-left-controls">
                        {/* Play/Pause Button */}
                        <button className="yt-control-btn play-pause-btn" onClick={togglePlay}>
                            {isPlaying ? (
                                <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
                                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                                </svg>
                            ) : (
                                <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
                                    <path d="M8 5v14l11-7z"/>
                                </svg>
                            )}
                        </button>

                        {/* Backward 10s */}
                        <button className="yt-control-btn seek-btn" onClick={() => seek(-10)} title="Rewind 10s">
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="white">
                                <path d="M12.5 3c-4.97 0-9 4.03-9 9H1l4.5 4.5L10 12H6.5c0-3.31 2.69-6 6-6s6 2.69 6 6-2.69 6-6 6c-1.66 0-3.17-.67-4.24-1.76l-1.42 1.42C8.71 18.04 10.5 19 12.5 19c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/>
                                <text x="50%" y="56%" dominantBaseline="middle" textAnchor="middle" fontSize="6.5" fontWeight="bold" fill="white">10</text>
                            </svg>
                        </button>

                        {/* Forward 10s */}
                        <button className="yt-control-btn seek-btn" onClick={() => seek(10)} title="Forward 10s">
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="white">
                                <path d="M11.5 3c4.97 0 9 4.03 9 9H23l-4.5 4.5L14 12h3.5c0-3.31-2.69-6-6-6s-6 2.69-6 6 2.69 6 6 6c1.66 0 3.17-.67 4.24-1.76l1.42 1.42C15.29 18.04 13.5 19 11.5 19c-4.97 0-9-4.03-9-9s4.03-9 9-9z"/>
                                <text x="50%" y="56%" dominantBaseline="middle" textAnchor="middle" fontSize="6.5" fontWeight="bold" fill="white">10</text>
                            </svg>
                        </button>

                        {/* Next Video Button */}
                        {hasNext && (
                            <button className="yt-control-btn next-btn" onClick={onNext} title="Next video">
                                <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
                                    <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
                                </svg>
                            </button>
                        )}

                        {/* Volume Control */}
                        <div className="yt-volume-control-group">
                            <button className="yt-control-btn volume-btn" onClick={toggleMute}>
                                {isMuted || volume === 0 ? (
                                    <svg viewBox="0 0 24 24" width="22" height="22" fill="white">
                                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.21.05-.42.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                                    </svg>
                                ) : volume < 0.5 ? (
                                    <svg viewBox="0 0 24 24" width="22" height="22" fill="white">
                                        <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>
                                    </svg>
                                ) : (
                                    <svg viewBox="0 0 24 24" width="22" height="22" fill="white">
                                        <path d="M3 9v6h4l5 5V4L9 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                    </svg>
                                )}
                            </button>
                            <input 
                                type="range" 
                                className="yt-volume-slider" 
                                min="0" 
                                max="1" 
                                step="0.05"
                                value={isMuted ? 0 : volume}
                                onChange={handleVolumeSlider}
                            />
                        </div>

                        {/* Time Display */}
                        <div className="yt-time-display">
                            <span>{formatTime(currentTime)}</span>
                            <span className="time-divider">/</span>
                            <span>{formatTime(duration)}</span>
                        </div>
                    </div>

                    <div className="yt-right-controls">
                        {/* Playback Speed Setting */}
                        <div className="yt-speed-control-group">
                            <button className="yt-control-btn speed-setting-btn" onClick={() => setShowSpeedMenu(!showSpeedMenu)} title="Playback Speed">
                                <svg viewBox="0 0 24 24" width="22" height="22" fill="white">
                                    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
                                </svg>
                                <span className="speed-badge">{playbackRate === 1 ? 'Normal' : `${playbackRate}x`}</span>
                            </button>
                            {showSpeedMenu && (
                                <div className="yt-speed-dropdown">
                                    {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                                        <div 
                                            key={rate} 
                                            className={`yt-speed-option ${playbackRate === rate ? 'active' : ''}`}
                                            onClick={() => handleSpeedChange(rate)}
                                        >
                                            {rate === 1 ? 'Normal' : `${rate}x`}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Fullscreen Button */}
                        <button className="yt-control-btn fullscreen-btn" onClick={toggleFullscreen}>
                            {isFullscreen ? (
                                <svg viewBox="0 0 24 24" width="22" height="22" fill="white">
                                    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                                </svg>
                            ) : (
                                <svg viewBox="0 0 24 24" width="22" height="22" fill="white">
                                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TrainingPage = () => {
    const navigate = useNavigate();
    const { user, token, logout, updateUserPathway } = useAuth();
    const authToken = token || localStorage.getItem(ACADEMY_AUTH_TOKEN_KEY) || '';
    const userIdentifier = user?.email || user?.username || 'STENOPE_ACADEMY_USER';
    const [view, setView] = useState('select'); // 'select' | 'pdf' | 'video'
    const [currentPath, setCurrentPath] = useState(user?.academyPathway || null); 
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [isLightTheme, setIsLightTheme] = useState(() => localStorage.getItem('theme') === 'light');
    const [showMobileIndex, setShowMobileIndex] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [numPages, setNumPages] = useState(0);
    const [zoom, setZoom] = useState(1.0);
    const [pdfError, setPdfError] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [isBlurred, setIsBlurred] = useState(false);
    const [collapsedSections, setCollapsedSections] = useState({
      "Part 1: Welcome & Universal Setup": false,
      "Part 2: Core Training Modes": false,
      "Part 3: Shared Tools & Utilities": false,
      "Part 4: Troubleshooting & Legal": false
    });

    const [videoPlaylists, setVideoPlaylists] = useState([]);
    const [videoLoading, setVideoLoading] = useState(false);
    const [activePlaylistIdx, setActivePlaylistIdx] = useState(0);
    const [activeVideoIdx, setActiveVideoIdx] = useState(0);
    const [collapsedPlaylists, setCollapsedPlaylists] = useState({});
    const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState({ videos: [], documents: [] });
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [searchHighlightIdx, setSearchHighlightIdx] = useState(-1);
    const [showMobileSearch, setShowMobileSearch] = useState(false);

    const menuRef = useRef(null);
    const blurTimeoutRef = useRef(null);
    const playerAreaRef = useRef(null);
    const searchRef = useRef(null);
    const searchInputRef = useRef(null);
    const mobileSearchInputRef = useRef(null);

    const toggleSection = (sectionTitle) => {
      setCollapsedSections(prev => ({
        ...prev,
        [sectionTitle]: !prev[sectionTitle]
      }));
    };

    const togglePlaylist = (title) => {
        setCollapsedPlaylists(prev => ({
            ...prev,
            [title]: !prev[title]
        }));
    };

    const selectVideo = (playlistIdx, videoIdx) => {
        setActivePlaylistIdx(playlistIdx);
        setActiveVideoIdx(videoIdx);
        setIsDetailsExpanded(false);
    };

    const currentVideo = (videoPlaylists.length > 0 && videoPlaylists[activePlaylistIdx]?.videos?.[activeVideoIdx]) || null;
    const API_URL = getBackendUrl();
    const streamUrl = currentVideo
        ? `${API_URL}/api/academy/video/stream/${videoPlaylists[activePlaylistIdx]?.originalIndex ?? activePlaylistIdx}/${activeVideoIdx}?token=${encodeURIComponent(authToken)}`
        : '';
    const pageImageUrl = `${API_URL}/api/academy/pdf/page/${currentPage}?doc=${currentPath}&token=${encodeURIComponent(authToken)}`;

    const getInitials = () => {
        if (user?.profile?.firstName || user?.profile?.lastName) {
            const first = (user.profile.firstName || '').charAt(0).toUpperCase();
            const last = (user.profile.lastName || '').charAt(0).toUpperCase();
            return `${first}${last}` || 'U';
        }
        if (user?.username) {
            return user.username.slice(0, 2).toUpperCase();
        }
        if (user?.email) {
            return user.email.slice(0, 2).toUpperCase();
        }
        return 'US';
    };

    const handleLogout = async () => {
        socketService.disconnect();
        await logout();
        navigate('/login', { replace: true });
    };

    const handlePathwaySelect = async (pathway) => {
        try {
            await authService.updatePathway(pathway);
            updateUserPathway(pathway);
            setCurrentPath(pathway);
            setView('select');
            setVideoPlaylists([]);
            setToastMessage(`Switched to ${pathway === 'voice' ? 'Stenope Voice' : 'Digital Reporter'}`);
        } catch (err) {
            console.error("Failed to set pathway:", err);
        }
    };

    const handlePathwayToggle = async (pathway) => {
        if (currentPath === pathway) return;
        await handlePathwaySelect(pathway);
    };

    useEffect(() => {
        if (user?.academyPathway) {
            setCurrentPath(user.academyPathway);
        } else {
            setCurrentPath(null);
        }
    }, [user?.academyPathway]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSearchResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Universal search across videos & PDF index
    const performSearch = useCallback((query) => {
        if (!query || query.trim().length < 2) {
            setSearchResults({ videos: [], documents: [] });
            setShowSearchResults(false);
            return;
        }

        const q = query.toLowerCase().trim();

        const videoResults = [];
        videoPlaylists.forEach((playlist, pIdx) => {
            playlist.videos.forEach((video, vIdx) => {
                const titleMatch = video.title.toLowerCase().includes(q);
                const descMatch = video.description?.toLowerCase().includes(q);
                const takeawayMatch = video.takeaways?.some(t => t.toLowerCase().includes(q));

                if (titleMatch || descMatch || takeawayMatch) {
                    videoResults.push({
                        type: 'video',
                        title: video.title,
                        subtitle: playlist.title,
                        duration: video.duration,
                        playlistIdx: pIdx,
                        videoIdx: vIdx,
                    });
                }
            });
        });

        const docSections = (currentPath === 'voice') ? voiceDocumentIndexSections : documentIndexSections;
        const docResults = [];
        docSections.forEach(section => {
            section.items?.forEach(item => {
                if (item.title.toLowerCase().includes(q)) {
                    docResults.push({
                        type: 'document',
                        title: item.title,
                        subtitle: section.title,
                        page: item.page,
                    });
                }
            });
        });

        setSearchResults({
            videos: videoResults.slice(0, 5),
            documents: docResults.slice(0, 5)
        });
        setShowSearchResults(videoResults.length > 0 || docResults.length > 0);
        setSearchHighlightIdx(-1);
    }, [videoPlaylists, currentPath]);

    useEffect(() => {
        const timer = setTimeout(() => {
            performSearch(searchQuery);
        }, 250);
        return () => clearTimeout(timer);
    }, [searchQuery, performSearch]);

    const handleSearchResultClick = (result) => {
        setSearchQuery('');
        setShowSearchResults(false);
        setShowMobileSearch(false);

        if (result.type === 'video') {
            setView('video');
            selectVideo(result.playlistIdx, result.videoIdx);
            const targetPlaylist = videoPlaylists[result.playlistIdx];
            if (targetPlaylist) {
                setCollapsedPlaylists(prev => ({
                    ...prev,
                    [targetPlaylist.title]: false
                }));
            }
            setTimeout(() => {
                if (playerAreaRef.current) {
                    playerAreaRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 200);
        } else if (result.type === 'document') {
            setView('pdf');
            setCurrentPage(result.page);
        }
    };

    const handleSearchKeyDown = (e) => {
        const allResults = [...searchResults.videos, ...searchResults.documents];
        if (!showSearchResults || allResults.length === 0) {
            if (e.key === 'Escape') {
                setShowMobileSearch(false);
                searchInputRef.current?.blur();
            }
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSearchHighlightIdx(prev => Math.min(prev + 1, allResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSearchHighlightIdx(prev => Math.max(prev - 1, -1));
        } else if (e.key === 'Enter' && searchHighlightIdx >= 0) {
            e.preventDefault();
            handleSearchResultClick(allResults[searchHighlightIdx]);
        } else if (e.key === 'Escape') {
            setShowSearchResults(false);
            setShowMobileSearch(false);
            searchInputRef.current?.blur();
        }
    };

    const renderSearchDropdown = () => {
        const allResults = [...searchResults.videos, ...searchResults.documents];
        if (!showSearchResults || allResults.length === 0) {
            if (searchQuery.trim().length >= 2) {
                return (
                    <div className="universal-search-dropdown">
                        <div className="search-empty">
                            <span>🔍</span>
                            <p>No results found for "{searchQuery}"</p>
                        </div>
                    </div>
                );
            }
            return null;
        }

        let flatIdx = 0;
        return (
            <div className="universal-search-dropdown">
                {searchResults.videos.length > 0 && (
                    <div className="search-result-category">
                        <span className="search-category-label">🎬 VIDEOS</span>
                        {searchResults.videos.map((result) => {
                            const idx = flatIdx++;
                            return (
                                <div
                                    key={`v-${result.playlistIdx}-${result.videoIdx}`}
                                    className={`search-result-item ${searchHighlightIdx === idx ? 'active' : ''}`}
                                    onClick={() => handleSearchResultClick(result)}
                                    onMouseEnter={() => setSearchHighlightIdx(idx)}
                                >
                                    <div className="search-result-icon video-icon">▶</div>
                                    <div className="search-result-info">
                                        <span className="search-result-title">{result.title}</span>
                                        <span className="search-result-subtitle">{result.subtitle}</span>
                                    </div>
                                    <span className="search-result-badge">{result.duration}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
                {searchResults.documents.length > 0 && (
                    <div className="search-result-category">
                        <span className="search-category-label">📄 DOCUMENT PAGES</span>
                        {searchResults.documents.map((result) => {
                            const idx = flatIdx++;
                            return (
                                <div
                                    key={`d-${result.page}`}
                                    className={`search-result-item ${searchHighlightIdx === idx ? 'active' : ''}`}
                                    onClick={() => handleSearchResultClick(result)}
                                    onMouseEnter={() => setSearchHighlightIdx(idx)}
                                >
                                    <div className="search-result-icon doc-icon">📋</div>
                                    <div className="search-result-info">
                                        <span className="search-result-title">{result.title}</span>
                                        <span className="search-result-subtitle">{result.subtitle}</span>
                                    </div>
                                    <span className="search-result-badge">P. {result.page}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        if (toastMessage) {
            const timer = setTimeout(() => {
                setToastMessage('');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [toastMessage]);

    useEffect(() => {
        if (isLightTheme) {
            document.body.classList.add('light-theme');
            localStorage.setItem('theme', 'light');
        } else {
            document.body.classList.remove('light-theme');
            localStorage.setItem('theme', 'dark');
        }
    }, [isLightTheme]);

    useEffect(() => {
        if (authToken) {
            socketService.connect(authToken);
        }
        return () => {
            socketService.disconnect();
        };
    }, [authToken]);

    useEffect(() => {
        if (videoPlaylists.length > 0) return;

        const fetchPlaylists = async () => {
            setVideoLoading(true);
            try {
                const res = await fetch(`${API_URL}/api/academy/video/playlists`, {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                });
                const data = await res.json();
                if (data.status === 'success' && data.playlists) {
                    const playlistsWithIndex = data.playlists.map((pl, idx) => ({
                        ...pl,
                        originalIndex: idx
                    }));

                    let filteredPlaylists = playlistsWithIndex;
                    if (currentPath === 'voice') {
                        filteredPlaylists = playlistsWithIndex.filter(pl => !pl.title.toLowerCase().includes('cloud'));
                    }

                    filteredPlaylists = filteredPlaylists.map((pl, idx) => {
                        const renumbered = pl.title.replace(/^\d+\./, `${idx + 1}.`);
                        return { ...pl, title: renumbered };
                    });

                    setVideoPlaylists(filteredPlaylists);
                    const collapsed = {};
                    filteredPlaylists.forEach((pl, idx) => {
                        collapsed[pl.title] = idx !== 0;
                    });
                    setCollapsedPlaylists(collapsed);
                }
            } catch (err) {
                console.error('Failed to fetch video playlists:', err);
            } finally {
                setVideoLoading(false);
            }
        };

        fetchPlaylists();
    }, [authToken, videoPlaylists.length, API_URL, currentPath]);

    // Handle focus loss and visibility change for security
    useEffect(() => {
        if (view !== 'pdf') return;
        
        const handleBlur = () => setIsBlurred(true);
        const handleFocus = () => setIsBlurred(false);
        const handleVisibilityChange = () => {
            if (document.hidden) setIsBlurred(true);
        };

        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [view]);

    // Block keyboard copy/save/print shortcuts for security
    useEffect(() => {
        if (view !== 'pdf') return;

        const handleKeyDown = (e) => {
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const modifier = isMac ? e.metaKey : e.ctrlKey;
            
            const isInputTarget = e.target && (
                e.target.tagName === 'INPUT' || 
                e.target.tagName === 'TEXTAREA' || 
                e.target.isContentEditable ||
                e.target.closest('.universal-search-wrapper') ||
                e.target.closest('.mobile-search-overlay')
            );

            if (e.key === 'PrintScreen' || e.keyCode === 44) {
                setIsBlurred(true);
                try {
                    navigator.clipboard.writeText('');
                } catch (err) {}
                e.preventDefault();
                return;
            }

            if (
                (modifier && (e.key === 's' || e.key === 'S')) ||
                (modifier && (e.key === 'p' || e.key === 'P')) ||
                e.key === 'F12' ||
                (modifier && e.shiftKey && ['I', 'J', 'C', 'i', 'j', 'c'].includes(e.key))
            ) {
                setIsBlurred(true);
                e.preventDefault();
                alert("Security Alert: Saving, printing, copying, and inspecting are strictly disabled for this training guide.");
                return;
            }

            if (!isInputTarget && modifier && (e.key === 'c' || e.key === 'C')) {
                setIsBlurred(true);
                e.preventDefault();
                alert("Security Alert: Copying is strictly disabled for this training guide.");
                return;
            }

            if (isInputTarget) {
                return;
            }

            setIsBlurred(true);

            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
            blurTimeoutRef.current = setTimeout(() => {
                if (document.hasFocus()) {
                    setIsBlurred(false);
                }
            }, 2000);
        };

        const handleKeyUp = (e) => {
            const isInputTarget = e.target && (
                e.target.tagName === 'INPUT' || 
                e.target.tagName === 'TEXTAREA' || 
                e.target.isContentEditable ||
                e.target.closest('.universal-search-wrapper') ||
                e.target.closest('.mobile-search-overlay')
            );
            if (isInputTarget) return;

            if (!document.hasFocus()) {
                setIsBlurred(true);
                if (blurTimeoutRef.current) {
                    clearTimeout(blurTimeoutRef.current);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, [view]);

    // Fetch secure PDF metadata from backend
    useEffect(() => {
        if (view !== 'pdf' || !currentPath) return;

        let isMounted = true;
        const fetchPdfMetadata = async () => {
            setPdfLoading(true);
            setPdfError(false);
            try {
                const res = await fetch(`${API_URL}/api/academy/pdf/metadata?doc=${currentPath}`, {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                });
                const data = await res.json();
                if (isMounted) {
                    if (data.status === 'success' && data.numPages) {
                        setNumPages(data.numPages);
                        setCurrentPage(1);
                        setPdfError(false);
                    } else {
                        console.warn("Failed to load PDF metadata from backend.");
                        setPdfError(true);
                    }
                }
            } catch (err) {
                console.warn("Error fetching PDF metadata.", err);
                if (isMounted) {
                    setPdfError(true);
                }
            } finally {
                if (isMounted) {
                    setPdfLoading(false);
                }
            }
        };

        fetchPdfMetadata();

        return () => {
            isMounted = false;
        };
    }, [view, currentPath, authToken, API_URL]);

    const handleBackToSelect = () => {
        setView('select');
    };

    const renderPlaylistContent = () => (
        <div className="video-playlist">
            <h4>COURSE PATHWAY</h4>
            <div className="playlist-items-grouped">
                {videoPlaylists.map((playlist, pIdx) => {
                    const isCollapsed = collapsedPlaylists[playlist.title];
                    const hasActiveVideo = activePlaylistIdx === pIdx;
                    
                    return (
                        <div key={pIdx} className="playlist-section-group">
                            <div 
                                className={`playlist-section-header ${hasActiveVideo ? 'active-playlist' : ''}`}
                                onClick={() => togglePlaylist(playlist.title)}
                            >
                                <span className="playlist-header-title">{playlist.title}</span>
                                <span className="playlist-header-arrow">{isCollapsed ? '▶' : '▼'}</span>
                            </div>
                            
                            {!isCollapsed && (
                                <div className="playlist-items-submenu">
                                    {playlist.videos.map((vid, vIdx) => {
                                        const isActive = activePlaylistIdx === pIdx && activeVideoIdx === vIdx;
                                        return (
                                            <div 
                                                key={vIdx} 
                                                className={`playlist-item ${isActive ? 'active' : ''}`} 
                                                onClick={() => {
                                                    selectVideo(pIdx, vIdx);
                                                    if (playerAreaRef.current) {
                                                        playerAreaRef.current.scrollIntoView({ behavior: 'smooth' });
                                                    }
                                                }}
                                            >
                                                <div className="playlist-item-status">
                                                    <span className="play-triangle">▶</span>
                                                </div>
                                                <div className="playlist-item-info">
                                                    <h5>{vid.title}</h5>
                                                    <span>{vid.duration}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const renderDetailsContent = () => {
        if (!currentVideo) return null;
        return (
            <div className={`video-meta-box ${isDetailsExpanded ? 'expanded' : 'collapsed'}`}>
                <div className="video-meta-toggle" onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}>
                    <span className="toggle-arrow">{isDetailsExpanded ? '▼' : '▶'}</span>
                    <h3>{currentVideo.title}</h3>
                    <span className="toggle-hint">{isDetailsExpanded ? 'Hide Details' : 'Show Details'}</span>
                </div>
                
                {isDetailsExpanded && (
                    <div className="video-meta-content fade-in">
                        <p className="video-description">{currentVideo.description}</p>
                        <div className="video-takeaways">
                            <h4>KEY TAKEAWAYS</h4>
                            <ul>
                                {currentVideo.takeaways.map((takeaway, idx) => (
                                    <li key={idx}>✔️ {takeaway}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={`training-page view-${view}`} onContextMenu={(e) => { if (view === 'pdf') e.preventDefault(); }}>
            <div className="particles">
                {[...Array(20)].map((_, i) => (
                    <span key={i} style={{
                        '--x': Math.random(),
                        '--y': Math.random(),
                        '--size': Math.random() * 4 + 2 + 'px',
                        '--duration': Math.random() * 15 + 20 + 's',
                        '--delay': Math.random() * 5 + 's',
                        background: ['#9d59ff', '#ff6b9d', '#00d4ff'][Math.floor(Math.random() * 3)]
                    }}></span>
                ))}
            </div>

            {showMobileSearch && (
                <div className="mobile-search-overlay fade-in">
                    <div className="mobile-search-header">
                        <button 
                            className="mobile-search-back-btn"
                            onClick={() => {
                                setShowMobileSearch(false);
                                setSearchQuery('');
                                setShowSearchResults(false);
                            }}
                            title="Back"
                        >
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="19" y1="12" x2="5" y2="12"></line>
                                <polyline points="12 19 5 12 12 5"></polyline>
                            </svg>
                        </button>
                        <div className="mobile-search-input-container">
                            <input
                                ref={mobileSearchInputRef}
                                type="text"
                                className="mobile-search-input"
                                placeholder="Search videos, guides, manual topics..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onFocus={() => {
                                    if (view === 'pdf') setIsBlurred(false);
                                }}
                                onKeyDown={handleSearchKeyDown}
                                autoFocus
                            />
                            {searchQuery && (
                                <button 
                                    className="search-clear-btn" 
                                    onClick={() => { 
                                        setSearchQuery(''); 
                                        setShowSearchResults(false); 
                                        mobileSearchInputRef.current?.focus(); 
                                    }}
                                    title="Clear"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="mobile-search-results-container">
                        {renderSearchDropdown()}
                    </div>
                </div>
            )}

            {/* HEADER AREA */}
            <div className="training-navbar">
                <div className="navbar-logo" onClick={() => { setView('select'); }} style={{ cursor: 'pointer' }}>
                    <img src="/favicon.png" alt="Stenope Logo" className="navbar-logo-img" />
                    <div className="navbar-logo-text-group">
                        <span className="navbar-logo-main">Stenope.AI</span>
                        <span className="navbar-logo-sub">A Product By Tisa Legal Media</span>
                    </div>
                </div>

                <div className="universal-search-wrapper desktop-search" ref={searchRef}>
                    <div className="universal-search-bar">
                        <svg className="search-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="universal-search-input"
                            placeholder="Search videos, guides, manual topics..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => {
                                if (view === 'pdf') setIsBlurred(false);
                                if (searchQuery.trim().length >= 2) setShowSearchResults(true);
                            }}
                            onKeyDown={handleSearchKeyDown}
                        />
                        {searchQuery && (
                            <button 
                                className="search-clear-btn" 
                                onClick={() => { 
                                    setSearchQuery(''); 
                                    setShowSearchResults(false); 
                                    searchInputRef.current?.focus(); 
                                }}
                                title="Clear search"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                    {renderSearchDropdown()}
                </div>

                <div className="navbar-actions" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <button 
                        className="mobile-search-trigger-btn"
                        onClick={() => {
                            setShowMobileSearch(true);
                            setTimeout(() => mobileSearchInputRef.current?.focus(), 100);
                        }}
                        title="Search"
                    >
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                    </button>
                    {/* Segmented Pathway Toggle */}
                    {user && user.academyPathway && (
                        <div className="pathway-toggle-segmented">
                            <div className={`segmented-bg ${currentPath === 'voice' ? 'slide-right' : ''}`}></div>
                            <button 
                                className={`toggle-pill-btn ${currentPath === 'reporter' ? 'active' : ''}`}
                                onClick={() => handlePathwayToggle('reporter')}
                            >
                                📝 Digital Reporter
                            </button>
                            <button 
                                className={`toggle-pill-btn ${currentPath === 'voice' ? 'active' : ''}`}
                                onClick={() => handlePathwayToggle('voice')}
                            >
                                🎙️ Stenope Voice
                            </button>
                        </div>
                    )}

                    {view !== 'select' && (
                        <button className="nav-back-btn" onClick={handleBackToSelect}>
                            <svg className="back-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="19" y1="12" x2="5" y2="12"></line>
                                <polyline points="12 19 5 12 12 5"></polyline>
                            </svg>
                            <span>Exit Module</span>
                        </button>
                    )}
                    
                    <div className="user-profile-menu-container" ref={menuRef}>
                        <div 
                            className={`user-avatar-circle ${showUserMenu ? 'active' : ''}`}
                            onClick={() => setShowUserMenu(!showUserMenu)}
                            title={user?.username || 'User Profile'}
                        >
                            {getInitials()}
                        </div>
                        
                        {showUserMenu && (
                            <div className="user-dropdown-menu fade-in">
                                <div className="user-dropdown-info">
                                    <span className="user-dropdown-name">
                                        {user?.profile?.firstName ? `${user.profile.firstName} ${user.profile.lastName || ''}` : user?.username}
                                    </span>
                                    <span className="user-dropdown-email">{user?.email}</span>
                                </div>
                                <div className="user-dropdown-divider"></div>
                                
                                {/* Option to go to Main App */}
                                <a 
                                    href="https://stenope.ai/app" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="dropdown-action-link"
                                    onClick={() => setShowUserMenu(false)}
                                >
                                    <svg className="menu-icon main-app-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '18px', height: '18px' }}>
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                        <polyline points="15 3 21 3 21 9"></polyline>
                                        <line x1="10" y1="14" x2="21" y2="3"></line>
                                    </svg>
                                    <span>Go to Main App</span>
                                </a>
                                
                                {/* Theme Switcher (Moved Above) */}
                                <div className="user-dropdown-theme-toggle">
                                    <button 
                                        className="theme-toggle-action-btn"
                                        onClick={() => setIsLightTheme(!isLightTheme)}
                                    >
                                        {isLightTheme ? (
                                            <>
                                                <svg className="menu-icon theme-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                                                </svg>
                                                <span>Dark Mode</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg className="menu-icon theme-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="5"></circle>
                                                    <line x1="12" y1="1" x2="12" y2="3"></line>
                                                    <line x1="12" y1="21" x2="12" y2="23"></line>
                                                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                                                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                                                    <line x1="1" y1="12" x2="3" y2="12"></line>
                                                    <line x1="21" y1="12" x2="23" y2="12"></line>
                                                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                                                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                                                </svg>
                                                <span>Light Mode</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                                
                                <div className="user-dropdown-divider"></div>
                                
                                {/* Logout Button (Moved Below to Prevent Accidental Clicks) */}
                                <button className="dropdown-logout-btn" onClick={() => { setShowUserMenu(false); handleLogout(); }}>
                                    <svg className="menu-icon logout-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                        <polyline points="16 17 21 12 16 7"></polyline>
                                        <line x1="21" y1="12" x2="9" y2="12"></line>
                                    </svg>
                                    <span>Logout</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="training-main-container">
                {/* 1. MODULES SELECTION SCREEN */}
                {view === 'select' && (
                    <div className={`selection-view fade-in ${!user?.academyPathway ? 'blurred-active' : ''}`}>
                        {/* Header */}
                        <div className="selection-header">
                            <h1>{(currentPath || 'reporter') === 'voice' ? 'Stenope Voice Pathway' : 'Digital Reporter Pathway'}</h1>
                            <p>Select a module below to start learning and tracking your progress.</p>
                        </div>
                        
                        {/* 3-Column Grid */}
                        <div className="selection-grid-new">
                            {/* Card 1: PDF Guide */}
                            <div className="selection-card-new pdf-card" onClick={() => { if (user?.academyPathway) setView('pdf'); }}>
                                <div className="card-top-accent pdf-accent"></div>
                                <div className="card-icon-container">
                                    <img src="/guide.png" alt="PDF Guide" className="card-icon-img" />
                                </div>
                                <span className="card-tag pdf-tag">🔒 DOCUMENT INDEX</span>
                                <h2>{(currentPath || 'reporter') === 'voice' ? 'Read Voice Guide' : 'Read Stenope Guide'}</h2>
                                <p>Detail {(currentPath || 'reporter') === 'voice' ? 'Stenope Voice' : 'Stenope.AI'} documentation and user guides.</p>
                                <div className="card-footer-new">
                                    <span className="duration-tag">⏱️ 45 min read</span>
                                    <button className="card-action-btn pdf-btn" disabled={!user?.academyPathway}>Open Guide →</button>
                                </div>
                            </div>
                            
                            {/* Card 2: Videos */}
                            <div className="selection-card-new video-card" onClick={() => { if (user?.academyPathway) setView('video'); }}>
                                <div className="card-top-accent video-accent"></div>
                                <div className="card-icon-container">
                                    <img src="/video.png" alt="Videos" className="card-icon-img" />
                                </div>
                                <span className="card-tag video-tag">💡 VIDEO COURSE</span>
                                <h2>Watch Video Guides</h2>
                                <p>Watch our video detailed tutorials to learn how to use {(currentPath || 'reporter') === 'voice' ? 'Stenope Voice' : 'Stenope.AI'} effectively.</p>
                                <div className="card-footer-new">
                                    <span className="duration-tag">⏱️ {(currentPath || 'reporter') === 'voice' ? '45 min videos' : '60 min videos'}</span>
                                    <button className="card-action-btn video-btn" disabled={!user?.academyPathway}>Open Player →</button>
                                </div>
                            </div>

                            {/* Card 3: Support & Help */}
                            <SupportCard />
                        </div>
                    </div>
                )}

                {/* 2. SECURE PDF VIEWER VIEW */}
                {view === 'pdf' && (
                    <div className={`pdf-workspace fade-in ${isBlurred ? 'blurred-active' : ''}`}>
                        {/* Security Blur Overlay */}
                        {isBlurred && (
                            <div className="security-blur-overlay">
                                <div className="blur-overlay-content">
                                    <div className="security-glowing-ring">
                                        <div className="pulse-ring"></div>
                                        <span className="lock-icon-svg">
                                            <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="#ff6b9d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                            </svg>
                                        </span>
                                    </div>
                                    <h2 className="security-title">Security Shield Active</h2>
                                    <div className="security-divider"></div>
                                    <p className="security-text">The training manual is temporarily hidden because the active window lost focus.</p>
                                    <p className="security-action">Click anywhere inside this window to restore reading mode</p>
                                </div>
                            </div>
                        )}

                        {pdfLoading ? (
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '480px', color: '#9ca3af', gap: '15px' }}>
                                <div className="video-loading-spinner-container" style={{ width: '60px', height: '60px' }}>
                                    <div className="video-loading-spinner-ring"></div>
                                    <div className="video-loading-spinner-ring inner"></div>
                                </div>
                                <p style={{ fontSize: '1.1rem' }}>Loading training manual...</p>
                            </div>
                        ) : pdfError ? (
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '480px', color: '#9ca3af', gap: '15px', textAlign: 'center', padding: '0 20px' }}>
                                <span style={{ fontSize: '3rem' }}>❌</span>
                                <h3 style={{ color: 'white', margin: '0' }}>Unable to Load</h3>
                                <p style={{ fontSize: '0.95rem', maxWidth: '450px', margin: '0', color: '#8a99ad' }}>Please try again later.</p>
                                <button className="nav-back-btn" onClick={handleBackToSelect} style={{ marginTop: '10px' }}>
                                    ← Back to Selection
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Document Title & Toolbar */}
                                <div className="pdf-toolbar">
                                    <div className="toolbar-info">
                                        <h3>{currentPath === 'voice' ? 'Stenope_Voice_Manual.pdf' : 'Stenope_Training_Manual.pdf'}</h3>
                                        <span className="confidential-badge">CONFIDENTIAL</span>
                                    </div>
                                    <div className="toolbar-controls">
                                        <button className="tool-btn" onClick={() => setZoom(z => Math.max(0.7, z - 0.1))} title="Zoom Out">-</button>
                                        <span className="zoom-value">{Math.round(zoom * 100)}%</span>
                                        <button className="tool-btn" onClick={() => setZoom(z => Math.min(1.5, z + 0.1))} title="Zoom In">+</button>
                                        <span className="divider">|</span>
                                        <span className="page-counter">Page {currentPage} of {numPages}</span>
                                    </div>
                                </div>

                                <div className="pdf-layout-body">
                                    {/* Table of Contents Side Panel / Sliding Drawer on Mobile */}
                                    <div className={`pdf-sidebar-index ${showMobileIndex ? 'mobile-open' : ''}`}>
                                        <div className="mobile-drawer-header">
                                            <h4>DOCUMENT INDEX</h4>
                                            <button className="close-drawer-btn" onClick={() => setShowMobileIndex(false)}>✕</button>
                                        </div>
                                        <h4 className="desktop-index-title">DOCUMENT INDEX</h4>
                                        <ul className="sidebar-index-list">
                                            {(currentPath === 'voice' ? voiceDocumentIndexSections : documentIndexSections).map((section, sIdx) => {
                                                const isCollapsed = collapsedSections[section.title];
                                                const isSectionActive = section.items.some(item => item.page === currentPage) || section.page === currentPage;
                                                
                                                return (
                                                    <div key={sIdx} className="sidebar-section-group">
                                                        <div 
                                                            className={`sidebar-index-header ${isSectionActive ? 'section-active' : ''}`}
                                                            onClick={() => toggleSection(section.title)}
                                                        >
                                                            <span className="collapse-arrow">{isCollapsed ? '▶' : '▼'}</span>
                                                            <span className="header-title-text">{section.title}</span>
                                                        </div>
                                                        {!isCollapsed && (
                                                            <div className="sidebar-items-submenu">
                                                                {section.items.map((item, iIdx) => (
                                                                    <li 
                                                                        key={iIdx} 
                                                                        className={`sidebar-index-item ${currentPage === item.page ? 'active' : ''}`} 
                                                                        onClick={() => {
                                                                            setCurrentPage(item.page);
                                                                            setShowMobileIndex(false); // Auto close mobile drawer
                                                                        }}
                                                                    >
                                                                        {item.title}
                                                                    </li>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </ul>
                                        <div className="security-terms-warning">
                                            <h5>SECURITY DIRECTIVES</h5>
                                            <p>Downloading, copying, printing, or screen recording of this content is restricted. User: {userIdentifier} is logged.</p>
                                        </div>
                                    </div>

                                    {showMobileIndex && (
                                        <div className="mobile-drawer-overlay" onClick={() => setShowMobileIndex(false)}></div>
                                    )}

                                    {/* Document Paper Container */}
                                    <div className="pdf-page-viewport">
                                        {/* Floating Menu Button for mobile */}
                                        <button className="mobile-floating-index-btn" onClick={() => setShowMobileIndex(true)}>
                                            ☰ Document Index
                                        </button>
                                        <div className="pdf-paper-page" style={{ width: `${95 * zoom}%`, maxWidth: `calc(1100px * ${zoom})` }}>
                                            {/* Document Content (Backend secure PDF page image) */}
                                            <div className="pdf-content-wrapper">
                                                <div className="pdf-image-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                                                    <img 
                                                        src={pageImageUrl} 
                                                        alt={`Page ${currentPage}`} 
                                                        className="pdf-page-image" 
                                                        style={{ width: '100%', height: 'auto', display: 'block', pointerEvents: 'none' }} 
                                                        onContextMenu={(e) => e.preventDefault()}
                                                    />
                                                </div>
                                            </div>
                                            
                                            {/* Page Number footer inside physical sheet */}
                                            <div className="pdf-paper-footer">
                                                <span>Stenope.AI Training Guide</span>
                                            </div>
                                        </div>

                                        {/* Floating Glassmorphic Page Navigation */}
                                        <div className="pdf-floating-controls">
                                            <button 
                                                className="floating-nav-btn" 
                                                disabled={currentPage <= 1} 
                                                onClick={() => setCurrentPage(c => Math.max(1, c - 1))}
                                                title="Previous Page"
                                            >
                                                ◀ Previous Page
                                            </button>
                                            <div className="floating-page-indicator">
                                                <span className="current-page-num">{currentPage}</span>
                                                <span className="floating-page-divider">/</span>
                                                <span className="total-page-num">{numPages}</span>
                                            </div>
                                            <button 
                                                className="floating-nav-btn" 
                                                disabled={currentPage >= numPages} 
                                                onClick={() => setCurrentPage(c => Math.min(numPages, c + 1))}
                                                title="Next Page"
                                            >
                                                Next Page ▶
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* 3. VIDEO PLAYER VIEW */}
                {view === 'video' && (
                    <div className="video-workspace fade-in">
                        
                        {videoLoading ? (
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '480px', color: '#9ca3af', gap: '15px' }}>
                                <div className="video-loading-spinner-container" style={{ width: '60px', height: '60px' }}>
                                    <div className="video-loading-spinner-ring"></div>
                                    <div className="video-loading-spinner-ring inner"></div>
                                </div>
                                <p style={{ fontSize: '1.1rem' }}>Loading course content...</p>
                            </div>
                        ) : videoPlaylists.length === 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '480px', color: '#9ca3af', gap: '15px', textAlign: 'center', padding: '0 20px' }}>
                                <span style={{ fontSize: '3rem' }}>❌</span>
                                <h3 style={{ color: 'white', margin: '0' }}>Unable to Load</h3>
                                <p style={{ fontSize: '0.95rem', maxWidth: '450px', margin: '0', color: '#8a99ad' }}>Please try again later.</p>
                                <button className="nav-back-btn" onClick={handleBackToSelect} style={{ marginTop: '10px' }}>
                                    ← Back to Selection
                                </button>
                            </div>
                        ) : (
                        <div className="video-layout">
                            {/* Playlist Sidebar - Desktop Only */}
                            <div className="video-playlist-desktop-sidebar">
                                {renderPlaylistContent()}
                            </div>

                            {/* Player Screen */}
                            <div className="video-player-container">
                                <div className="video-player-frame" ref={playerAreaRef}>
                                    {streamUrl ? (
                                        <CustomVideoPlayer
                                            key={`${activePlaylistIdx}-${activeVideoIdx}`}
                                            src={streamUrl}
                                            hasNext={
                                                activePlaylistIdx < videoPlaylists.length - 1 || 
                                                activeVideoIdx < videoPlaylists[activePlaylistIdx]?.videos?.length - 1
                                            }
                                            onNext={() => {
                                                const currentPlaylist = videoPlaylists[activePlaylistIdx];
                                                if (activeVideoIdx < currentPlaylist.videos.length - 1) {
                                                    selectVideo(activePlaylistIdx, activeVideoIdx + 1);
                                                } else if (activePlaylistIdx < videoPlaylists.length - 1) {
                                                    selectVideo(activePlaylistIdx + 1, 0);
                                                }
                                            }}
                                        />
                                    ) : (
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%', color: '#9ca3af' }}>
                                            Loading video...
                                        </div>
                                    )}
                                </div>

                                {/* Mobile Only Stacked Content (Details followed by Course Pathway playlist) */}
                                <div className="video-mobile-stacked-content">
                                    {renderDetailsContent()}
                                    {renderPlaylistContent()}
                                </div>

                                {/* Desktop Only active video details */}
                                <div className="video-desktop-meta-wrapper">
                                    {renderDetailsContent()}
                                </div>

                                {/* Related Manual Pages */}
                                {(() => {
                                    const pdfMapping = currentVideo?.pdfPages?.[currentPath];
                                    if (!pdfMapping || !pdfMapping.pages || pdfMapping.pages.length === 0) return null;
                                    return (
                                        <div className="video-related-pdf" onContextMenu={(e) => e.preventDefault()}>
                                            <div className="video-related-pdf-header">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                    <polyline points="14 2 14 8 20 8"></polyline>
                                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                                    <polyline points="10 9 9 9 8 9"></polyline>
                                                </svg>
                                                <span>Related Manual Page{pdfMapping.pages.length > 1 ? 's' : ''}</span>
                                                <span className="video-related-pdf-badge">{pdfMapping.pages.length} page{pdfMapping.pages.length > 1 ? 's' : ''}</span>
                                            </div>
                                            <div className="video-related-pdf-pages">
                                                {pdfMapping.pages.map((pageNum) => (
                                                    <div key={pageNum} className="video-related-pdf-page-wrapper">
                                                        <div className="video-related-pdf-page-label">Page {pageNum}</div>
                                                        <div className="video-related-pdf-page">
                                                            <img
                                                                src={`${API_URL}/api/academy/pdf/page/${pageNum}?doc=${pdfMapping.doc}&token=${encodeURIComponent(authToken)}`}
                                                                alt={`Manual Page ${pageNum}`}
                                                                draggable={false}
                                                                onContextMenu={(e) => e.preventDefault()}
                                                                onError={(e) => { e.target.style.display = 'none'; }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                        )}
                    </div>
                )}
            </div>


            {toastMessage && (
                <div className="premium-toast-notification fade-in">
                    <svg className="toast-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <span>{toastMessage}</span>
                </div>
            )}

            {/* FIRST LOGIN PATHWAY POPUP MODAL */}
            {user && !user.academyPathway && (
                <div className="premium-modal-backdrop">
                    <div className="premium-modal-card">
                        <div className="modal-header">
                            <div className="modal-logo-container">
                                <img src="/favicon.png" alt="Stenope Logo" className="modal-logo" />
                            </div>
                            <h2>Choose Your Pathway</h2>
                            <p>Customize your Stenope Academy training workspace. Select the workflow that matches how you plan to use Stenope.</p>
                        </div>
                        <div className="modal-options-grid">
                            <div className="modal-option-card reporter-option" onClick={() => handlePathwaySelect('reporter')}>
                                <div className="option-glow reporter-glow"></div>
                                <div className="option-icon-container">
                                    <svg className="option-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#8B5CF6' }}>
                                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                                        <line x1="8" y1="21" x2="16" y2="21"></line>
                                        <line x1="12" y1="17" x2="12" y2="21"></line>
                                    </svg>
                                </div>
                                <h3>Digital Reporter</h3>
                                <p>Learn standard digital reporting, speaker identification, and transcript handoffs.</p>
                                <button className="option-select-btn reporter-btn">Select Pathway</button>
                            </div>
                            
                            <div className="modal-option-card voice-option" onClick={() => handlePathwaySelect('voice')}>
                                <div className="option-glow voice-glow"></div>
                                <div className="option-icon-container">
                                    <svg className="option-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#EC4899' }}>
                                        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"></path>
                                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                                        <line x1="12" y1="19" x2="12" y2="22"></line>
                                    </svg>
                                </div>
                                <h3>Stenope Voice</h3>
                                <p>Learn voice writing dictation best practices, voice commands, and speech engine settings.</p>
                                <button className="option-select-btn voice-btn">Select Pathway</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainingPage;
